import { CardData, Connection } from '../types';
import { convertLtToMinutes } from './calculations';

/**
 * Interface para resultados da simulação
 */
export interface SimulationResult {
  minLeadTime: number;
  maxLeadTime: number;
  p10LeadTime: number; // Percentil 10 (Melhor Caso)
  p90LeadTime: number; // Percentil 90 (Pior Caso - Risco Crítico)
  p50LeadTime: number; // Mediana
  meanLeadTime: number; // Média (Caso Esperado)
  stdDeviation: number; // Desvio padrão
  iterations: number;
  leadTimes: number[]; // Array com todos os lead times calculados
}

/**
 * Gera um número aleatório com distribuição triangular simplificada
 * Usa uma aproximação simples: média de dois números uniformes
 * Isso cria uma distribuição mais concentrada no centro (como triangular)
 */
const triangularRandom = (min: number, max: number, mode: number): number => {
  // Aproximação triangular usando média ponderada
  const u = Math.random();
  if (u < 0.5) {
    // Primeira metade: entre min e mode
    return min + (mode - min) * Math.sqrt(2 * u);
  } else {
    // Segunda metade: entre mode e max
    return mode + (max - mode) * (1 - Math.sqrt(2 * (1 - u)));
  }
};

/**
 * Aplica variação assimétrica em um valor usando distribuição triangular
 * @param baseValue - Valor base
 * @param minPercent - Percentual mínimo de redução (ex: -15)
 * @param maxPercent - Percentual máximo de aumento (ex: +25)
 */
const applyVariation = (baseValue: number, minPercent: number = -15, maxPercent: number = 25): number => {
  if (baseValue <= 0) return 0;
  
  const min = baseValue * (1 + minPercent / 100);
  const max = baseValue * (1 + maxPercent / 100);
  const mode = baseValue; // Moda no valor base
  
  return Math.max(0, triangularRandom(min, max, mode));
};

/**
 * Aplica variação de +/- 20% em um valor usando distribuição triangular (compatibilidade)
 */
const applyVariationSymmetric = (baseValue: number, variationPercent: number = 20): number => {
  return applyVariation(baseValue, -variationPercent, variationPercent);
};

/**
 * Calcula o Lead Time Total (longest path) para um conjunto de cards
 * Versão simplificada focada apenas no caminho mais longo
 */
const calculateLongestPath = (cards: CardData[]): number => {
  // Filtrar apenas cards ativos e não anotações
  const activeCards = cards.filter(c => c.status === 'ativo' && c.tipo !== 'anotacao');
  const startNode = activeCards.find(c => c.tipo === 'card_inicial');
  
  if (!startNode) return 0;

  // Criar mapa de cards para acesso rápido
  const cardMap = new Map<string, CardData>();
  activeCards.forEach(c => cardMap.set(c.id, c));

  const completedPaths: { time: number; pathIds: string[] }[] = [];

  // Função recursiva para percorrer o grafo
  const traverse = (currentId: string, currentSum: number, visited: string[]): void => {
    if (visited.includes(currentId)) {
      // Loop detectado, parar
      return;
    }

    const card = cardMap.get(currentId);
    if (!card) return;

    const newVisited = [...visited, currentId];
    
    // Calcular carga do card (tempo de processamento)
    let cardLoad = 0;
    if (card.tipo !== 'card_inicial' && card.status !== 'removido') {
      // Usar conversão segura (Fix Gemini)
      const ltValue = Number(card.lt) || 0;
      const tpInMinutes = convertLtToMinutes(ltValue, card.tpUnit);
      const tra = Number(card.tt) || 0; // TRA já está em minutos
      const pessoas = Number(card.pessoasSimultaneas) || 1;
      
      // Carga = (TP + TRA) / Pessoas
      cardLoad = pessoas > 0 ? (tpInMinutes + tra) / pessoas : 0;
    }

    const newSum = currentSum + cardLoad;

    // Se não tem conexões de saída, é um nó terminal
    if (card.conexoes_saida.length === 0) {
      completedPaths.push({ time: newSum, pathIds: newVisited });
      return;
    }

    // Percorrer todas as conexões de saída
    card.conexoes_saida.forEach(conn => {
      let branchProbability = 1.0;
      
      // Se for decisão, usar probabilidade
      if (card.tipo === 'decisao') {
        if (conn.sourceHandle === 'right') {
          branchProbability = (card.decisao_sim_pct || 0) / 100;
        } else {
          branchProbability = (card.decisao_nao_pct || 0) / 100;
        }
      }
      
      // Só seguir caminhos com probabilidade > 0
      if (branchProbability > 0) {
        traverse(conn.targetId, newSum, newVisited);
      }
    });
  };

  // Iniciar travessia a partir do nó inicial
  traverse(startNode.id, 0, []);

  if (completedPaths.length === 0) return 0;

  // Retornar o maior tempo (longest path)
  const maxPath = completedPaths.reduce((prev, current) => 
    (prev.time > current.time) ? prev : current
  );

  return maxPath.time;
};

/**
 * Calcula percentil de um array ordenado
 */
const percentile = (sortedArray: number[], p: number): number => {
  if (sortedArray.length === 0) return 0;
  if (sortedArray.length === 1) return sortedArray[0];
  
  const index = (p / 100) * (sortedArray.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;

  if (upper >= sortedArray.length) return sortedArray[sortedArray.length - 1];
  
  return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
};

/**
 * Executa simulação de Monte Carlo para análise de variabilidade (compatibilidade)
 * 
 * @param cards - Array de cards do fluxo
 * @param iterations - Número de iterações (padrão: 1000)
 * @param variationPercent - Percentual de variação simétrica (padrão: 20%)
 * @returns Resultado da simulação com estatísticas
 */
export const runSimulation = (
  cards: CardData[],
  iterations: number = 1000,
  variationPercent: number = 20
): SimulationResult => {
  return simulateProcess(cards, iterations, -variationPercent, variationPercent);
};

/**
 * Executa simulação de Monte Carlo para análise de sensibilidade
 * Usa variação assimétrica para simular variabilidade real do chão de fábrica
 * 
 * @param cards - Array de cards do fluxo
 * @param iterations - Número de iterações (padrão: 500)
 * @param minVariation - Percentual mínimo de redução (padrão: -15%)
 * @param maxVariation - Percentual máximo de aumento (padrão: +25%)
 * @returns Resultado da simulação com estatísticas
 */
export const simulateProcess = (
  cards: CardData[],
  iterations: number = 500,
  minVariation: number = -15,
  maxVariation: number = 25
): SimulationResult => {
  const leadTimes: number[] = [];

  // Executar iterações
  for (let i = 0; i < iterations; i++) {
    // Criar cópia dos cards com tempos variados
    const variedCards: CardData[] = cards.map(card => {
      if (card.tipo === 'card_inicial' || card.status === 'removido') {
        return { ...card };
      }

      // Aplicar variação assimétrica nos tempos (TP e TRA)
      // Simula variabilidade real: mais fácil ter atrasos (+25%) do que adiantamentos (-15%)
      const variedLt = applyVariation(Number(card.lt) || 0, minVariation, maxVariation);
      const variedTt = applyVariation(Number(card.tt) || 0, minVariation, maxVariation);

      return {
        ...card,
        lt: variedLt,
        tt: variedTt,
      };
    });

    // Calcular longest path para esta iteração
    const leadTime = calculateLongestPath(variedCards);
    leadTimes.push(leadTime);
  }

  // Ordenar lead times para cálculo de percentis
  const sortedLeadTimes = [...leadTimes].sort((a, b) => a - b);

  // Calcular estatísticas
  const minLeadTime = sortedLeadTimes[0] || 0;
  const maxLeadTime = sortedLeadTimes[sortedLeadTimes.length - 1] || 0;
  const p10LeadTime = percentile(sortedLeadTimes, 10); // Melhor Caso
  const p90LeadTime = percentile(sortedLeadTimes, 90); // Pior Caso - Risco Crítico
  const p50LeadTime = percentile(sortedLeadTimes, 50); // Mediana
  const meanLeadTime = leadTimes.reduce((sum, val) => sum + val, 0) / leadTimes.length;

  // Calcular desvio padrão
  const variance = leadTimes.reduce((sum, val) => sum + Math.pow(val - meanLeadTime, 2), 0) / leadTimes.length;
  const stdDeviation = Math.sqrt(variance);

  return {
    minLeadTime,
    maxLeadTime,
    p10LeadTime,
    p90LeadTime,
    p50LeadTime,
    meanLeadTime,
    stdDeviation,
    iterations,
    leadTimes: sortedLeadTimes, // Retornar ordenado para possível visualização
  };
};
